import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useGetRolesListQuery } from "../user/userApi";
import {
  canAccessPath,
  findRoleDefinition,
  getLandingPath,
  getRoleRoutePermissions,
} from "./accessControl";
import {
  getAdminProfile,
  isAdminRole,
  updateStoredRoutePermissions,
} from "./authSession";

const AccessContext = createContext(null);

export const AccessProvider = ({ children }) => {
  const [profile, setProfile] = useState(() => getAdminProfile());

  useEffect(() => {
    const syncProfile = () => setProfile(getAdminProfile());

    window.addEventListener("auth-profile-changed", syncProfile);
    window.addEventListener("storage", syncProfile);

    return () => {
      window.removeEventListener("auth-profile-changed", syncProfile);
      window.removeEventListener("storage", syncProfile);
    };
  }, []);

  const shouldLoadRoles = Boolean(profile) && !isAdminRole(profile?.role);
  const { data, isFetching, isError, isUninitialized } = useGetRolesListQuery(
    undefined,
    { skip: !shouldLoadRoles },
  );

  const accessProfile = useMemo(() => {
    if (!profile) {
      return null;
    }

    if (isAdminRole(profile.role)) {
      return { ...profile, fullAccess: true, permissionsKnown: true };
    }

    if (data) {
      const match = findRoleDefinition(data.data, profile.role);

      if (match) {
        return {
          ...profile,
          routePermissions: getRoleRoutePermissions(match),
          fullAccess: false,
          permissionsKnown: true,
        };
      }

      return { ...profile, fullAccess: true, permissionsKnown: false };
    }

    if (Array.isArray(profile.routePermissions)) {
      return { ...profile, fullAccess: false, permissionsKnown: true };
    }

    if (isError) {
      return { ...profile, fullAccess: true, permissionsKnown: false };
    }

    return { ...profile, fullAccess: false, permissionsKnown: false, pending: true };
  }, [data, isError, profile]);

  useEffect(() => {
    if (!profile || isAdminRole(profile.role) || !data) {
      return;
    }

    const match = findRoleDefinition(data.data, profile.role);

    if (!match) {
      return;
    }

    const nextPermissions = getRoleRoutePermissions(match);

    if (
      JSON.stringify(profile.routePermissions ?? null) ===
      JSON.stringify(nextPermissions)
    ) {
      return;
    }

    updateStoredRoutePermissions(nextPermissions);
  }, [data, profile]);

  const isResolving = Boolean(
    accessProfile?.pending && (isUninitialized || isFetching || !data),
  );

  const value = useMemo(
    () => ({
      profile: accessProfile,
      isResolving,
      canAccess: (path) => canAccessPath(accessProfile, path),
      landingPath: getLandingPath(accessProfile),
    }),
    [accessProfile, isResolving],
  );

  return (
    <AccessContext.Provider value={value}>{children}</AccessContext.Provider>
  );
};

export const useAccess = () => {
  const context = useContext(AccessContext);

  if (!context) {
    throw new Error("useAccess must be used within AccessProvider");
  }

  return context;
};
